import React, { useEffect, useRef } from "react";
import useSearchStore from "./stores/useSearchStore";
import useInfiniteScroll from "react-infinite-scroll-hook";
import Select from "./components/Select";
import defaultCompanyLogo from "../../images/company-logo-05.png";

const datePostedOptions = [
  { value: "all", label: "Anytime" },
  { value: "today", label: "Today" },
  { value: "3days", label: "Last 3 Days" },
  { value: "week", label: "Last Week" },
  { value: "month", label: "Last Month" },
];

const employmentTypesOptions = [
  { value: "all", label: "All", id: "radio-6" },
  { value: "fulltime", label: "Full Time", id: "radio-1" },
  { value: "partime", label: "Part Time", id: "radio-2" },
  { value: "contractor", label: "Contract", id: "radio-3" },
  { value: "intern", label: "Internship", id: "radio-4" },
  { value: "temporary", label: "Temporary", id: "radio-5" },
];

const JobSearch = ({ ky, lc, dp, et, re }) => {
  const {
    q,
    l,
    employment_types,
    date_posted,
    work_from_home,
    jobs,
    jobsLoading,
    hasMoreJobs,
    setField,
    fetchJobs,
    loadMoreJobs,
    error,
  } = useSearchStore();

  const [infiniteRef] = useInfiniteScroll({
    loading: jobsLoading,
    hasNextPage: hasMoreJobs,
    onLoadMore: async () => {
      if (!jobsLoading && hasMoreJobs && jobs.length > 0) {
        loadMoreJobs();
      }
    },
    disabled: !hasMoreJobs,
    rootMargin: "0px 0px 200px 0px",
  });

  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      if (ky) setField("q", ky);
      if (lc) setField("l", lc);
      if (dp) setField("date_posted", dp);
      if (et) setField("employment_types", [et]);
      if (re) setField("work_from_home", re === "true");

      fetchJobs({
        q: ky,
        l: lc,
        date_posted: dp,
        employment_types: et ? [et] : [],
        work_from_home: re === "true",
      });
      didInit.current = true;
    }
    // eslint-disable-next-line
  }, [ky, lc, dp, et, re]);

  const buildSearchUrl = ({
    q,
    l,
    date_posted,
    employment_types,
    work_from_home,
  }) => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (l) params.append("l", l);
    if (date_posted && date_posted !== "all") params.append("dp", date_posted);
    if (
      employment_types &&
      employment_types.length > 0 &&
      employment_types[0] !== "all"
    )
      params.append("et", employment_types[0]);
    if (work_from_home) params.append("re", "true");

    const paramString = params.toString();
    return paramString ? `/jobs/search?${paramString}` : `/jobs/search`;
  };

  const handleInputChange = (e) => {
    setField(e.target.name, e.target.value);
  };

  const handleRadioChange = (e) => {
    setField("employment_types", [e.target.value]);
  };

  const handleSubmit = (e = null) => {
    e?.preventDefault();
    const url = buildSearchUrl({
      q,
      l,
      date_posted,
      employment_types,
      work_from_home,
    });
    window.history.replaceState({}, "", url);
    fetchJobs();
  };

  const handleDatePostedChange = (e) => {
    setField("date_posted", e.target.value);
    const url = buildSearchUrl({
      q,
      l,
      date_posted: e.target.value,
      employment_types,
      work_from_home,
    });
    window.history.replaceState({}, "", url);
    fetchJobs({ date_posted: e.target.value });
  };

  const handleWorkFromHomeChange = (e) => {
    setField("work_from_home", e.target.checked);
    const url = buildSearchUrl({
      q,
      l,
      date_posted,
      employment_types,
      work_from_home: e.target.checked,
    });
    window.history.replaceState({}, "", url);
    fetchJobs({ work_from_home: e.target.checked });
  };

  return (
    <div className="full-page-container">
      <div className="full-page-sidebar">
        <form onSubmit={handleSubmit} method="get" action="/jobs/search">
          <div className="full-page-sidebar-inner" data-simplebar>
            <div className="sidebar-container">
              {/* Location */}
              <div className="sidebar-widget">
                <h3>Location</h3>
                <div className="input-with-icon">
                  <div id="autocomplete-container">
                    <input
                      type="text"
                      id="autocomplete-input"
                      name="l"
                      placeholder="City"
                      value={l || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <i className="icon-material-outline-location-on"></i>
                </div>
              </div>

              {/* Keywords */}
              <div className="sidebar-widget">
                <h3>Keywords</h3>
                <div className="keywords-container-1">
                  <div className="keyword-input-container">
                    <input
                      type="text"
                      className="keyword-input"
                      name="q"
                      placeholder="Job Title or Keywords"
                      value={q || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              {/* Job Types */}
              <div className="sidebar-widget hide-on-mobile">
                <h3>Job Type</h3>
                <div className="switches-list">
                  {employmentTypesOptions.map((type) => (
                    <React.Fragment key={type.value}>
                      <div className="radio">
                        <input
                          type="radio"
                          name="employment_types"
                          id={type.id}
                          value={type.value}
                          checked={
                            Array.isArray(employment_types) &&
                            employment_types[0] === type.value
                          }
                          onChange={handleRadioChange}
                        />
                        <label htmlFor={type.id}>
                          <span className="radio-label"></span> {type.label}
                        </label>
                      </div>
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Search Button */}
              <div className="sidebar-search-button-container">
                <button className="button ripple-effect" name="">
                  Search
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Full Page Content */}
      <div className="full-page-content-container" data-simplebar>
        <div className="full-page-content-inner">
          <h3 className="page-title">Search Results</h3>
          <div className="notify-box margin-top-15">
            <div className="switch-container">
              <label className="switch">
                <input
                  type="checkbox"
                  id="search-alert-check"
                  checked={!!work_from_home}
                  onChange={handleWorkFromHomeChange}
                />
                <span className="switch-button"></span>
                <span className="switch-text">Remote</span>
              </label>
            </div>
            <div className="sort-by">
              <span>Date Posted:</span>
              <Select
                options={datePostedOptions}
                value={date_posted || "all"}
                onChange={handleDatePostedChange}
                id="search-sort-select"
                name="date_posted"
                placeholder="Posted Anytime"
                liveSearch={false} // set true if you want search
                className="selectpicker hide-tick"
              />
            </div>
          </div>

          <div className="listings-container margin-top-35">
            {!jobsLoading && jobs.length === 0 && (
              <div className="notification warning">
                <p className="text-center">
                  Your search query did not match any jobs. Please rephrase or
                  broaden your query and try again.
                </p>
              </div>
            )}
            {jobs.map((job) => (
              <a
                key={job.job_id}
                href={`/jobs/${job.job_id}`}
                data-turbo="false"
                role="button"
                className="job-listing"
              >
                <div className="job-listing-details">
                  <div className="job-listing-company-logo">
                    <img
                      src={job.employer_logo || defaultCompanyLogo}
                      alt={job.employer_name}
                    />
                  </div>
                  <div className="job-listing-description">
                    <h4 className="job-listing-company">{job.employer_name}</h4>
                    <h3 className="job-listing-title">{job.job_title}</h3>
                    <p className="job-listing-text">
                      {job.job_description?.slice(0, 200)}
                      {job.job_description && job.job_description.length > 200
                        ? "..."
                        : ""}
                    </p>
                  </div>
                </div>
                <div className="job-listing-footer">
                  <ul>
                    <li>
                      <i className="icon-material-outline-location-on"></i>{" "}
                      {job.job_location}
                    </li>
                    <li>
                      <i className="icon-material-outline-access-time"></i>{" "}
                      {job.job_posted_at}
                    </li>
                  </ul>
                </div>
              </a>
            ))}
            {jobsLoading && (
              <div className="listings-loader">
                {/* Loader skeleton */}
                <div className="job-listing">
                  <div>
                    <div className="ph-item">
                      <div className="ph-col-2 job-listing-company-logo">
                        <div className="ph-avatar"></div>
                      </div>
                      <div>
                        <div className="ph-row">
                          <div className="ph-col-2"></div>
                          <div className="ph-col-10 empty"></div>
                          <div className="ph-col-4 big"></div>
                          <div className="ph-col-8 big empty"></div>
                          <div className="ph-col-12 empty"></div>
                          <div className="ph-col-12"></div>
                          <div className="ph-col-8"></div>
                          <div className="ph-col-4 empty"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ph-footer">
                    <div className="ph-item">
                      <div>
                        <div className="ph-row">
                          <div className="ph-col-2"></div>
                          <div className="ph-col-10 empty"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {error && (
            <div className="notification warning">
              <p className="text-center">{error}</p>
            </div>
          )}
          <div ref={infiniteRef}></div>
        </div>
      </div>
    </div>
  );
};

export default JobSearch;
